import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { FaEdit } from 'react-icons/fa';
import { MdCancel } from 'react-icons/md';
import { RxUpdate } from 'react-icons/rx';
import { toast } from 'react-toastify';
import usePostalCodesActions from '../../../hooks/APIHooks/users/usePostalCodesActions';
import useCheckUserFeaturePermissions from '../../../hooks/useCheckUserFeaturePermissions';
import { PostalCode, PostalCodeReqBody } from '../../../types/postalCodes';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AcademiaSectionWrapper from '../AcademiaSectionWrapper';
import SkillBadge from '../Badges/SkillBadge';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaSearchInput from '../Inputs/AcademiaSearchInput';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const PostalCodeDetailsModal = ({
  postalCode,
  handleModalClose,
}: {
  postalCode: PostalCode;
  handleModalClose: () => void;
}) => {
  // integration of react Hooks here
  const [postalCodeList, setPostalCodeList] = useState<Array<string>>([]);
  const [newPostalCodeList, setNewPostalCodeList] = useState<Array<string>>([]);

  const [newPostalCode, setNewPostalCode] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');

  const [isPostalCodeInvalid, setIsPostalCodeInvalid] =
    useState<boolean>(false);
  const [enableUpdating, setEnableUpdating] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  // integration of custom API hooks here
  const { updatePostalCodeByIdHandler } = usePostalCodesActions('');

  // integration of custom hooks here
  const { featureBasedPermissions } =
    useCheckUserFeaturePermissions('cityPostalCode');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // fallback to default values when updating cancelled here
  useMemo(() => {
    if (!enableUpdating) {
      setPostalCodeList(postalCode.postal_codes);
      setNewPostalCode('');
      setNewPostalCodeList([]);
    }
  }, [enableUpdating, postalCode]);

  // filtering postal codes based on search text here
  useMemo(() => {
    setPostalCodeList(
      postalCode.postal_codes.filter((code) =>
        searchText
          ? code.toLowerCase().includes(searchText.toLowerCase())
          : code
      )
    );
  }, [postalCode, searchText]);

  const updatePostalCodesHandler = async () => {
    try {
      setIsUpdating(true);

      const body: PostalCodeReqBody = {
        city: postalCode.city,
        postal_codes: postalCodeList.concat(newPostalCodeList),
        updated_by: '',
      };

      await updatePostalCodeByIdHandler
        .mutateAsync({ postalCodeId: postalCode.id, body })
        .then((res) => {
          if (res.is_success) {
            toast.success('Postal Codes Updated!!');

            handleModalClose();
            queryClient.invalidateQueries({ queryKey: ['postal-code-list'] });
          } else {
            toast.error(res.message || 'Postal Code Update Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsUpdating(false);
    }
  };

  // function to remove postal codes from list declared here
  const removePostalCodeFromList = (
    code: string,
    setterFn: React.Dispatch<React.SetStateAction<Array<string>>>
  ) => {
    setterFn((state) => state.filter((codeFromList) => codeFromList !== code));
  };

  // rendering postal code details modal component here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[650px]'>
      <ModalTitle titleText='postal code details' />

      <div className='space-y-5'>
        <div>
          <div className='flex items-center gap-1 italic text-legal-dark-blue text-lg'>
            <p>City</p>
            <p>:</p>
            <p className='font-semibold'>{postalCode.city_name}</p>
          </div>

          <div className='flex items-center gap-1 italic text-legal-dark-blue text-lg'>
            <p>Province</p>
            <p>:</p>
            <p className='font-semibold'>{postalCode.province_name}</p>
          </div>
        </div>

        <AcademiaSectionWrapper
          sectionTitle='Postal Codes'
          additionalClassName='!mt-7'
        >
          <div className='flex flex-col md:flex-row items-center justify-between gap-2 pb-3 mb-3 border-b-2 border-dashed border-legal-dark-blue'>
            <div className='w-1/5 min-w-full md:min-w-[220px]'>
              <AcademiaSearchInput
                searchText={searchText}
                setSearchText={setSearchText}
                inputLabel='Search Postal Code...'
              />
            </div>

            <AcademiaButton
              title={
                !featureBasedPermissions?.includes('UPDATE')
                  ? 'You do not have permissions!'
                  : !enableUpdating
                  ? 'Enable Update'
                  : 'Disable Update'
              }
              type='button'
              onClick={() => setEnableUpdating((state) => !state)}
              icon={
                !enableUpdating ? (
                  <FaEdit className='size-6' />
                ) : (
                  <MdCancel className='size-6' />
                )
              }
              variant={!enableUpdating ? 'primary' : 'warning'}
              additionalClassNames='!py-2 w-full md:w-fit'
              isDisabled={!featureBasedPermissions?.includes('UPDATE')}
            >
              {!enableUpdating ? 'Update' : 'cancel'}
            </AcademiaButton>
          </div>

          <div className='space-y-2'>
            {enableUpdating ? (
              <>
                <motion.div
                  initial={{ x: 0 }}
                  animate={
                    isPostalCodeInvalid
                      ? {
                          x: [0, -10, 0, 10, 0],
                          transition: {
                            duration: 0.1,
                            repeat: 2,
                          },
                        }
                      : { x: 0 }
                  }
                >
                  <label
                    htmlFor='input-postal-code'
                    className='block mb-1 text-legal-dark-blue font-medium text-base'
                  >
                    {'New Postal Code'}
                    {<span className='text-base-red'>*</span>}
                  </label>

                  <input
                    type='text'
                    id='input-postal-code'
                    className={`border-2 focus:ring-4 focus:ring-legal-dark-blue outline-legal-dark-blue rounded-md w-full p-3 text-legal-dark-blue font-semibold duration-300 text-base disabled:opacity-40 disabled:cursor-not-allowed read-only:opacity-60 read-only:focus:ring-0 ${
                      isPostalCodeInvalid
                        ? 'border-base-red'
                        : 'border-legal-dark-blue'
                    }`}
                    placeholder='Enter new postal code here...'
                    value={newPostalCode}
                    onChange={(e) => {
                      setNewPostalCode(e.target.value);
                      setIsPostalCodeInvalid(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newPostalCode) {
                        if (
                          newPostalCode.length < 5 ||
                          newPostalCode.length > 10
                        ) {
                          setIsPostalCodeInvalid(true);
                        } else {
                          const allPostalCodes =
                            postalCodeList.concat(newPostalCodeList);

                          const existingPostalCode = allPostalCodes.find(
                            (code) => code.trim() === newPostalCode.trim()
                          );

                          if (existingPostalCode) {
                            toast.warn('Postal Code Already Exists!!');
                          } else {
                            setNewPostalCodeList((state) => [
                              ...state,
                              newPostalCode.trim(),
                            ]);
                            setNewPostalCode('');
                          }
                        }
                      }
                    }}
                    autoComplete='off'
                  />

                  <motion.label
                    initial={{ y: -20, opacity: 0, display: 'none' }}
                    animate={
                      isPostalCodeInvalid
                        ? { y: 0, opacity: 1, display: 'block' }
                        : {
                            y: -20,
                            opacity: 0,
                            transitionEnd: { display: 'none' },
                          }
                    }
                    htmlFor='input-postal-code'
                    className='block mt-1 text-base-red text-base'
                  >
                    {newPostalCode.length < 5
                      ? 'Postal code must have at least 5 characters!'
                      : newPostalCode.length > 10
                      ? 'Postal code cannot have more than 10 characters!'
                      : null}
                  </motion.label>
                </motion.div>

                {newPostalCodeList.length ? (
                  <div className='space-y-2'>
                    {enableUpdating && (
                      <h5 className='text-xl text-legal-dark-blue italic border-b-2 border-dashed border-legal-dark-blue'>
                        New Postal Codes
                      </h5>
                    )}

                    <div className='flex items-center flex-wrap gap-2'>
                      {newPostalCodeList.map((code, index) => (
                        <SkillBadge
                          key={index}
                          badgeLabel={code}
                          color='#FC7300'
                          isEditing={enableUpdating}
                          onClickRemove={() =>
                            removePostalCodeFromList(code, setNewPostalCodeList)
                          }
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            ) : null}

            <div className='space-y-2'>
              {enableUpdating && (
                <h5 className='text-xl text-legal-dark-blue italic border-b-2 border-dashed border-legal-dark-blue'>
                  Existing Postal Codes
                </h5>
              )}

              <div className='max-h-[400px] overflow-y-auto academia-scrollbar'>
                {postalCodeList.length ? (
                  <div className='flex items-center flex-wrap gap-2 '>
                    {postalCodeList.map((code, index) => (
                      <SkillBadge
                        key={index}
                        badgeLabel={code}
                        color='#4CB9E7'
                        isEditing={enableUpdating}
                        onClickRemove={() =>
                          removePostalCodeFromList(code, setPostalCodeList)
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <p className='text-center font-semibold text-base-red'>
                    No Postal Code Found!!
                  </p>
                )}
              </div>
            </div>
          </div>
        </AcademiaSectionWrapper>

        {enableUpdating && (
          <AcademiaButton
            type='button'
            onClick={updatePostalCodesHandler}
            icon={<RxUpdate className='size-6' />}
            variant='premium'
            isLoading={isUpdating}
            fullWidth
          >
            Update Postal Codes
          </AcademiaButton>
        )}
      </div>
    </ParentModal>
  );
};

export default PostalCodeDetailsModal;
